import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import { categories } from "@/mocks/categories";

const principles = [
  {
    icon: "ri-flashlight-fill",
    title: "每日更新",
    text: "坚持每日同步连载漫画最新话，今日更新准时上线，让你追更漫画不迷路。",
  },
  {
    icon: "ri-search-eye-fill",
    title: "精准检索",
    text: "想看哪部直接搜，支持名称、标签与分类多维检索，轻松找到心仪作品。",
  },
  {
    icon: "ri-star-fill",
    title: "精选推荐",
    text: "由编辑与读者共同把关，持续推荐画风、剧情与更新质量俱佳的连载佳作。",
  },
  {
    icon: "ri-user-heart-fill",
    title: "阅读体验",
    text: "关注阅读流畅度与清晰度，随时随地打开连载漫画，体验流畅舒适。",
  },
];

const timeline = [
  { year: "2022", event: "天天漫画网上线，专注连载漫画每日更新" },
  { year: "2023", event: "漫库分类与检索系统全面升级" },
  { year: "2024", event: "读者追更与推荐机制上线，口碑快速积累" },
  { year: "2026", event: "覆盖全题材连载漫画，成为追更党的日常选择" },
];

const aboutLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于天天漫画网",
  url: "https://panelplay1.github.io/about",
  inLanguage: "zh-CN",
  description: "了解天天漫画网的品牌介绍、发展理念、内容原则与联系方式。",
};

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd) }} />
      <Navbar />
      <main>
        <section className="bg-foreground-950 py-16 text-center">
          <div className="mx-auto max-w-3xl px-4">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-background-200/30 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-100">
              <i className="ri-information-line text-secondary-400"></i>关于我们
            </p>
            <h1 className="font-heading text-3xl text-background-50 md:text-4xl">
              让追更漫画，变得简单又准时
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
              天天漫画网是一个专注连载漫画每日更新的内容平台。我们希望每一位读者都能在这里轻松找到想看的漫画，掌握最新进度，不再错过任何最新话。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-bold tracking-widest text-primary-500 uppercase">
                网站定位
                <span className="h-px w-6 bg-primary-400" />
              </p>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
                每日更新的连载漫画内容门户
              </h2>
              <p className="mt-4 leading-relaxed text-foreground-700">
                从热血冒险到恋爱治愈，从奇幻仙侠到科幻末世，我们精选热门连载漫画，坚持每日更新最新话。无论你是资深追更党，还是刚入坑的新读者，都能在这里快速找到感兴趣的作品，随时点开随时看。
              </p>
              <p className="mt-4 leading-relaxed text-foreground-600">
                我们相信，好的内容应该被更多人看见。通过分类、检索与推荐，我们希望帮你缩短找漫画的时间，把更多时间留给真正好看的连载故事。
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link to="/list" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-book-2-fill"></i>去漫库逛逛
                </Link>
                <Link to="/faq" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600">
                  常见问题
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {categories.slice(0, 4).map((c, i) => (
                <Link
                  key={c.id}
                  to={`/list?category=${c.id}`}
                  className="group flex flex-col items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-6 transition-all hover:-translate-y-1 hover:border-primary-300"
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${i % 2 === 0 ? "bg-primary-100 text-primary-600" : "bg-accent-100 text-accent-600"}`}>
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <p className="text-sm font-bold text-foreground-900">{c.name}</p>
                  <p className="text-xs text-foreground-500">{c.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-8">
              <p className="mb-2 flex items-center gap-2 text-xs font-bold tracking-widest text-primary-500 uppercase">
                内容原则
                <span className="h-px w-6 bg-primary-400" />
              </p>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们坚持的四件事</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p, i) => (
                <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${i % 3 === 0 ? "bg-primary-100 text-primary-600" : i % 3 === 1 ? "bg-accent-100 text-accent-600" : "bg-secondary-100 text-secondary-700"}`}>
                    <i className={`${p.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 text-sm font-bold text-foreground-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <div className="mb-8">
            <p className="mb-2 flex items-center gap-2 text-xs font-bold tracking-widest text-primary-500 uppercase">
              发展历程
              <span className="h-px w-6 bg-primary-400" />
            </p>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">一路陪伴追更党</h2>
          </div>
          <ol className="relative space-y-6 border-l-2 border-primary-200 pl-8">
            {timeline.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[41px] top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 ring-4 ring-background-50">
                  <i className="ri-check-line text-[10px] text-background-50"></i>
                </span>
                <p className="font-heading text-lg text-primary-600">{t.year}</p>
                <p className="text-sm text-foreground-700">{t.event}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-background-100 py-14">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-6">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">有任何想法或建议？</h2>
            <p className="mt-3 text-sm text-foreground-600">
              欢迎通过以下方式联系我们，我们会认真阅读每一条反馈。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a href="mailto:service@tiantian-mh.example.com" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-mail-line"></i>service@tiantian-mh.example.com
              </a>
              <Link to="/list" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600">
                <i className="ri-book-2-fill"></i>去漫库
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}